import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { Membership, Person } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { useLiveVersion } from '../../shared/live';

type Similar = { personId: string; name: string; birthDate: string | null; family: string | null };
type Row = Membership & { person: Person | null; similar?: Similar[] };

/** Solicitudes de Membresía pendientes de la Iglesia activa. */
export function MembershipRequestsPage() {
  const me = useMe();
  return (
    <div className="page">
      <header className="page__header">
        <h1>Solicitudes de membresía</h1>
        {me.activeContext ? <p className="muted">{me.activeContext.churchName}</p> : null}
      </header>
      <MembershipRequests />
    </div>
  );
}

/** Lista de solicitudes pendientes con Aprobar / Rechazar (también se usa dentro de Miembros). */
export function MembershipRequests({ onDecided, title, hideWhenEmpty = false }: { onDecided?: () => void; title?: string; hideWhenEmpty?: boolean }) {
  const me = useMe();
  const churchId = me.activeChurchId;
  const live = useLiveVersion((e) => e.kind.startsWith('membership'));
  const requests = useAsync(
    (signal) =>
      churchId
        ? api.get<{ items: Row[] }>(`/multichurch/churches/${churchId}/memberships?status=pending`, signal).then((r) => r.items)
        : Promise.resolve([]),
    [churchId, live],
  );
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  /** Ya tenía ficha en la iglesia: su cuenta se queda con ella (familia, membresía, historia). */
  async function link(row: Row, ficha: Similar) {
    setBusyId(row.id);
    setFeedback(null);
    try {
      await api.post(`/multichurch/churches/${churchId}/memberships/${row.id}/link`, { personId: ficha.personId });
      setFeedback({ tone: 'success', text: `Listo: la cuenta quedó unida a la ficha de ${ficha.name} y ya es miembro.` });
      requests.reload();
      onDecided?.();
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusyId(null);
    }
  }

  async function decide(row: Row, action: 'approve' | 'reject') {
    if (action === 'reject' && reason.trim().length < 3) {
      setFeedback({ tone: 'error', text: 'Escribe el motivo del rechazo: la persona lo verá.' });
      return;
    }
    const name = row.person ? `${row.person.givenName} ${row.person.familyName}`.trim() : 'esta persona';
    setBusyId(row.id);
    setFeedback(null);
    try {
      await api.post(`/multichurch/churches/${churchId}/memberships/${row.id}/${action}`, action === 'reject' ? { reason: reason.trim() } : undefined);
      setRejecting(null);
      setReason('');
      setFeedback({ tone: 'success', text: action === 'approve' ? `${name} ahora es miembro.` : 'Solicitud rechazada.' });
      requests.reload();
      onDecided?.();
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusyId(null);
    }
  }

  if (hideWhenEmpty && !feedback && requests.status === 'ready' && requests.data.length === 0) return null;
  return (
    <>
      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
      <Card title={title}>
        {!churchId ? <EmptyState title="Elige primero una iglesia en el selector de arriba." /> : null}
        {churchId && requests.status === 'loading' ? <Loading /> : null}
        {requests.status === 'error' ? <ErrorState error={requests.error} onRetry={requests.reload} /> : null}
        {churchId && requests.status === 'ready' && requests.data.length === 0 ? <EmptyState title="No hay solicitudes pendientes." /> : null}
        {requests.status === 'ready' && requests.data.length > 0 ? (
          <ul className="list">
            {requests.data.map((row) => (
              <li key={row.id} className="list__item list__item--actions">
                <div>
                  <strong>{row.person ? `${row.person.givenName} ${row.person.familyName}` : 'Persona'}</strong>
                  <span className="muted"> · solicitó el {new Date(row.requested_at).toLocaleDateString('es')}</span>
                  {row.similar?.map((f) => (
                    <div key={f.personId} className="notice" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                      <p>
                        ¿Es esta persona? Ya tienes una ficha de <strong>{f.name}</strong>
                        {f.birthDate ? `, nació el ${new Date(`${f.birthDate}T12:00:00`).toLocaleDateString('es')}` : ''}
                        {f.family ? `, familia ${f.family}` : ''}.
                      </p>
                      <Button variant="secondary" busy={busyId === row.id} disabled={busyId !== null} onClick={() => link(row, f)}>
                        Sí, es la misma: unir y aprobar
                      </Button>
                    </div>
                  ))}
                </div>
                {rejecting === row.id ? (
                  <div className="reject-box">
                    <div className="field">
                      <label htmlFor={`reason-${row.id}`}>Motivo (obligatorio; lo verá la persona)</label>
                      <textarea id={`reason-${row.id}`} required minLength={3} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej. Acércate primero a la oficina pastoral." />
                    </div>
                    <div className="actions">
                      <Button variant="danger" busy={busyId === row.id} disabled={busyId !== null} onClick={() => decide(row, 'reject')}>
                        Rechazar solicitud
                      </Button>
                      <Button variant="ghost" onClick={() => setRejecting(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="actions">
                    <Button variant="secondary" onClick={() => setRejecting(row.id)} disabled={busyId !== null}>
                      Rechazar
                    </Button>
                    <Button onClick={() => decide(row, 'approve')} busy={busyId === row.id} disabled={busyId !== null}>
                      Aprobar
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    </>
  );
}
